/**
 * @name ForgotPasswordView.js
 * @description 비밀번호 찾기 - 로그인 모달 내부 전용 뷰
 * - 아이디 + 이름 + 전화번호 입력 후 임시 비밀번호 발급
 * - props로 네비게이션 처리 (페이지 이동 없음)
 */
import React, { useState } from 'react';
import { post } from '@utils/axios_api';
import { toast } from 'react-toastify';

const ForgotPasswordView = ({ onGoLogin, onGoForgotId }) => {
  const [form, setForm] = useState({ id: '', name: '', phoneNo: '' });
  const [tempPassword, setTempPassword] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const sanitized = name === 'phoneNo' ? value.replace(/\D/g, '') : value;
    setForm((prev) => ({ ...prev, [name]: sanitized }));
    setTempPassword(null);
    setErrorMsg('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.id || !form.name || !form.phoneNo) {
      setErrorMsg('아이디, 이름, 전화번호를 모두 입력해 주세요.');
      return;
    }
    if (!/^01[0-9]\d{7,8}$/.test(form.phoneNo)) {
      setErrorMsg('올바른 전화번호 형식이 아닙니다. (예: 01012345678)');
      return;
    }

    setIsLoading(true);
    try {
      const response = await post('/auth/forgot-password', form, {
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.result.success) {
        setTempPassword(response.result.tempPassword);
        setErrorMsg('');
      } else {
        setTempPassword(null);
        setErrorMsg(response.result.message || '입력하신 정보와 일치하는 계정이 없습니다.');
      }
    } catch (err) {
      console.error('비밀번호 찾기 오류:', err);
      setErrorMsg('서버 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = () => {
    if (!tempPassword) return;
    navigator.clipboard.writeText(tempPassword)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        toast.warn('복사에 실패했습니다. 비밀번호를 직접 메모해 주세요: ' + tempPassword);
      });
  };

  return (
    <div className="login-form" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <div className="logo text-center mb-3">
        <img src="/img/logo.png" alt="회사 로고" style={{ width: '100px', height: 'auto' }} />
      </div>
      <h1 className="text-center fs-4 fw-bold mb-3">비밀번호 찾기</h1>
      <p style={{ color: '#666', marginBottom: '16px', fontSize: '0.9rem', textAlign: 'center' }}>
        가입 시 등록한 아이디, 이름, 전화번호를 입력해 주세요.
      </p>

      {tempPassword && (
        <div style={{
          background: '#fff8e1',
          border: '1px solid #FFC107',
          borderRadius: '6px',
          padding: '16px',
          marginBottom: '16px',
          textAlign: 'center'
        }}>
          <p style={{ margin: 0, color: '#555', fontSize: '0.9rem' }}>임시 비밀번호가 발급되었습니다.</p>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', margin: '10px 0' }}>
            <span style={{ fontWeight: 'bold', fontSize: '1.4rem', color: '#e65100', letterSpacing: '2px' }}>
              {tempPassword}
            </span>
            <button
              type="button"
              onClick={handleCopy}
              style={{
                padding: '4px 10px', fontSize: '0.8rem', cursor: 'pointer',
                border: '1px solid #FFC107', borderRadius: '4px',
                background: copied ? '#FFC107' : '#fff', color: '#555'
              }}
            >
              {copied ? '복사됨!' : '복사'}
            </button>
          </div>
          <p style={{ margin: 0, color: '#e65100', fontSize: '0.85rem' }}>
            ⚠️ 로그인 후 반드시 비밀번호를 변경해 주세요.
          </p>
        </div>
      )}

      {errorMsg && (
        <p style={{ color: 'red', fontSize: '0.85rem', marginBottom: '12px' }}>{errorMsg}</p>
      )}

      {!tempPassword && (
        <form onSubmit={handleSubmit}>
          <div className="login_input_wrap">
            <input
              type="text"
              name="id"
              placeholder="아이디를 입력해 주세요."
              value={form.id}
              onChange={handleChange}
              maxLength="30"
            />
            <input
              type="text"
              name="name"
              placeholder="이름을 입력해 주세요."
              value={form.name}
              onChange={handleChange}
              maxLength="20"
            />
            <input
              type="text"
              name="phoneNo"
              placeholder="전화번호를 입력해 주세요. (예: 01012345678)"
              value={form.phoneNo}
              onChange={handleChange}
              maxLength="11"
            />
            <p style={{ fontSize: '11px', color: '#888', margin: '3px 0 0' }}>- 없이 숫자만 입력하세요.</p>
          </div>
          <button
            type="submit"
            className="login-button"
            disabled={isLoading}
            style={{ marginTop: '16px' }}
          >
            {isLoading ? '조회 중...' : '임시 비밀번호 발급'}
          </button>
        </form>
      )}

      {tempPassword && (
        <button
          type="button"
          className="login-button"
          onClick={onGoLogin}
          style={{ marginTop: '12px', background: '#1976D2', width: '100%' }}
        >
          로그인하러 가기
        </button>
      )}

      <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: 'auto', paddingTop: '12px', fontSize: '0.9rem' }}>
        <button type="button" onClick={onGoLogin} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer' }}>
          로그인
        </button>
        <span style={{ color: '#ddd' }}>|</span>
        <button type="button" onClick={onGoForgotId} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer' }}>
          아이디 찾기
        </button>
      </div>
    </div>
  );
};

export default ForgotPasswordView;
